interface Props {
  size?: number
  color?: string
  opacity?: number
  className?: string
  /** Number of concentric contour rings. Default 7. */
  rings?: number
}

/**
 * Depth contour pattern — concentric curved lines resembling bathymetric
 * (sea-depth) chart markings. Dramatic, used SPARINGLY:
 *   • Empty-state backgrounds
 *   • Error / 404 page backdrop
 *   • Login splash backdrop
 *   • 1-2 hero cards in marketing/landing
 *
 * Lines fade outward to feel like depth recedes into shallow water.
 * Place behind content with absolute positioning, opacity-controlled.
 */
export function DepthContour({ size = 600, color = 'currentColor', opacity = 0.12, className, rings = 7 }: Props) {
  const cx = size / 2
  const cy = size / 2

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ pointerEvents: 'none' }}
    >
      <defs>
        <radialGradient id="depthFade" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity={opacity} />
          <stop offset="70%" stopColor={color} stopOpacity={opacity * 0.6} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g fill="none" stroke="url(#depthFade)" strokeWidth="1.5">
        {Array.from({ length: rings }).map((_, i) => {
          const baseR = (size / 2) * ((i + 1) / (rings + 1))
          // Slight wobble per ring so contours feel organic, not perfectly circular
          const wobble = (i % 2 === 0 ? 1 : -1) * (i + 1) * 1.5
          const rx = baseR
          const ry = baseR + wobble
          return (
            <ellipse
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed concentric contours; index is identity
              key={i}
              cx={cx}
              cy={cy}
              rx={rx}
              ry={ry}
            />
          )
        })}
      </g>
    </svg>
  )
}
